package br.com.htms.inspeccaocampo.data.funcionarios

import br.com.htms.inspeccaocampo.data.os.PgrCargoOpcao
import br.com.htms.inspeccaocampo.data.os.PgrCatalog
import br.com.htms.inspeccaocampo.export.ZipLeitor
import br.com.htms.inspeccaocampo.export.ZipStoreWriter

/** Linha lida da planilha de importação, já validada. */
data class LinhaImportacao(
    /** Número da linha na planilha (1 = cabeçalho). */
    val linha: Int,
    val nome: String,
    val cpf: String,
    val matricula: String,
    val admissao: String,
    val cargo: PgrCargoOpcao?,
    val cargoTexto: String,
    val erros: List<String>,
    val existente: Funcionario?
) {
    val valida: Boolean get() = erros.isEmpty()
}

data class ResultadoImportacao(val linhas: List<LinhaImportacao>, val erroGeral: String? = null)

/**
 * Modelo .xlsx para cadastro em lote de colaboradores e leitura do arquivo preenchido
 * (.xlsx ou .csv). Os campos são os mesmos do cadastro do portal.
 */
object FuncionariosPlanilha {
    const val NOME_MODELO = "modelo_importacao_colaboradores.xlsx"
    const val MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    const val ABA_DADOS = "Colaboradores"
    const val ABA_CARGOS = "Cargos"
    private const val LINHAS_LISTA = 2000

    val CABECALHO = listOf(
        "Nome completo *",
        "CPF *",
        "Matrícula",
        "Data de admissão (dd/mm/aaaa)",
        "Cargo (PGR) - escolha na lista"
    )

    /** Texto exibido na lista suspensa: único por cargo + setor/GHE. */
    fun rotuloCargo(op: PgrCargoOpcao): String = "${op.cargo.nome} | ${op.setorGhe}"

    // ---------------------------------------------------------------- modelo

    fun gerarModelo(cargos: List<PgrCargoOpcao> = PgrCatalog.opcoes): ByteArray {
        val ultimaCargo = cargos.size + 1
        val dados = folha(
            linhas = listOf(CABECALHO),
            larguras = listOf(42.0, 18.0, 16.0, 30.0, 70.0),
            estiloColunas = 2,
            congelarCabecalho = true,
            extra = """<dataValidations count="1"><dataValidation type="list" allowBlank="1" showErrorMessage="1" """ +
                """errorTitle="Cargo inválido" error="Escolha um cargo da lista (aba Cargos)." """ +
                """showInputMessage="1" promptTitle="Cargo (PGR)" prompt="Clique na seta e escolha o cargo." """ +
                """sqref="E2:E$LINHAS_LISTA"><formula1>$ABA_CARGOS!${'$'}A${'$'}2:${'$'}A${'$'}$ultimaCargo</formula1>""" +
                """</dataValidation></dataValidations>"""
        )
        val listaCargos = folha(
            linhas = listOf(listOf("Opção da lista (cargo | setor/GHE)", "Cargo", "Setor / GHE")) +
                cargos.map { listOf(rotuloCargo(it), it.cargo.nome, it.setorGhe) },
            larguras = listOf(80.0, 40.0, 40.0),
            estiloColunas = 0,
            congelarCabecalho = true
        )
        val instrucoes = folha(
            linhas = listOf(
                listOf("Como preencher a planilha de importação de colaboradores"),
                listOf(""),
                listOf("1. Preencha uma linha por colaborador na aba \"$ABA_DADOS\" (não altere o cabeçalho)."),
                listOf("2. Nome completo e CPF são obrigatórios. O CPF pode ter pontos e traço ou só números."),
                listOf("3. Matrícula é opcional (até 40 caracteres)."),
                listOf("4. Data de admissão é opcional, no formato dd/mm/aaaa (ex.: 05/03/2024)."),
                listOf("5. Cargo: clique na célula da coluna E e escolha na lista. A lista é a mesma do portal (PGR)."),
                listOf("6. Se o CPF já estiver cadastrado, o portal pode atualizar o colaborador (você escolhe na importação)."),
                listOf("7. Salve como .xlsx (ou .csv) e use \"Importar planilha\" na tela de cadastro de funcionários."),
                listOf(""),
                listOf("Exemplo de linha:"),
                listOf("JOÃO DA SILVA | 123.456.789-09 | 1234 | 05/03/2024 | " +
                    (cargos.firstOrNull()?.let(::rotuloCargo) ?: "ELETRICISTA | SETOR - GHE 01"))
            ),
            larguras = listOf(120.0),
            estiloColunas = 0,
            congelarCabecalho = false,
            primeiraNegrito = true
        )
        val abas = listOf(ABA_DADOS, ABA_CARGOS, "Instruções")
        val workbook = buildString {
            append(XML_DECL)
            append("""<workbook xmlns="$NS_MAIN" xmlns:r="$NS_REL"><bookViews><workbookView activeTab="0"/></bookViews><sheets>""")
            abas.forEachIndexed { i, nome ->
                append("""<sheet name="${esc(nome)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>""")
            }
            append("</sheets></workbook>")
        }
        val workbookRels = buildString {
            append(XML_DECL)
            append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
            abas.indices.forEach { i ->
                append("""<Relationship Id="rId${i + 1}" Type="$NS_REL/worksheet" Target="worksheets/sheet${i + 1}.xml"/>""")
            }
            append("""<Relationship Id="rId${abas.size + 1}" Type="$NS_REL/styles" Target="styles.xml"/>""")
            append("</Relationships>")
        }
        val contentTypes = buildString {
            append(XML_DECL)
            append("""<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">""")
            append("""<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>""")
            append("""<Default Extension="xml" ContentType="application/xml"/>""")
            append("""<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>""")
            abas.indices.forEach { i ->
                append("""<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>""")
            }
            append("""<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>""")
            append("</Types>")
        }
        val rootRels = XML_DECL +
            """<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""" +
            """<Relationship Id="rId1" Type="$NS_REL/officeDocument" Target="xl/workbook.xml"/></Relationships>"""
        val entradas = linkedMapOf(
            "[Content_Types].xml" to contentTypes,
            "_rels/.rels" to rootRels,
            "xl/workbook.xml" to workbook,
            "xl/_rels/workbook.xml.rels" to workbookRels,
            "xl/styles.xml" to STYLES,
            "xl/worksheets/sheet1.xml" to dados,
            "xl/worksheets/sheet2.xml" to listaCargos,
            "xl/worksheets/sheet3.xml" to instrucoes
        )
        return ZipStoreWriter.write(entradas.mapValues { it.value.encodeToByteArray() })
    }

    private fun folha(
        linhas: List<List<String>>,
        larguras: List<Double>,
        estiloColunas: Int,
        congelarCabecalho: Boolean,
        primeiraNegrito: Boolean = true,
        extra: String = ""
    ): String = buildString {
        append(XML_DECL)
        append("""<worksheet xmlns="$NS_MAIN" xmlns:r="$NS_REL">""")
        append("""<sheetViews><sheetView workbookViewId="0">""")
        if (congelarCabecalho) {
            append("""<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>""")
        }
        append("</sheetView></sheetViews>")
        append("""<sheetFormatPr defaultRowHeight="15"/><cols>""")
        larguras.forEachIndexed { i, w ->
            val estilo = if (estiloColunas != 0) """ style="$estiloColunas"""" else ""
            append("""<col min="${i + 1}" max="${i + 1}" width="$w"$estilo customWidth="1"/>""")
        }
        append("</cols><sheetData>")
        linhas.forEachIndexed { r, cells ->
            append("""<row r="${r + 1}">""")
            cells.forEachIndexed { c, v ->
                val estilo = if (r == 0 && primeiraNegrito) 1 else estiloColunas
                append("""<c r="${coluna(c)}${r + 1}" s="$estilo" t="inlineStr"><is><t xml:space="preserve">${esc(v)}</t></is></c>""")
            }
            append("</row>")
        }
        append("</sheetData>")
        append(extra)
        append("</worksheet>")
    }

    private fun coluna(i: Int): String {
        var n = i + 1
        val sb = StringBuilder()
        while (n > 0) {
            val r = (n - 1) % 26
            sb.insert(0, ('A' + r))
            n = (n - 1) / 26
        }
        return sb.toString()
    }

    private fun esc(s: String): String = buildString(s.length) {
        for (ch in s) when (ch) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            else -> append(ch)
        }
    }

    private const val XML_DECL = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>"""
    private const val NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main"
    private const val NS_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships"

    /** Estilos: 0 padrão, 1 cabeçalho (negrito branco em azul), 2 texto (@) para CPF/datas não virarem número. */
    private val STYLES = XML_DECL +
        """<styleSheet xmlns="$NS_MAIN">""" +
        """<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>""" +
        """<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts>""" +
        """<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>""" +
        """<fill><patternFill patternType="solid"><fgColor rgb="FF1F4E78"/><bgColor indexed="64"/></patternFill></fill></fills>""" +
        """<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>""" +
        """<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>""" +
        """<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>""" +
        """<xf numFmtId="49" fontId="1" fillId="2" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>""" +
        """<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>""" +
        """<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>""" +
        """</styleSheet>"""

    // ---------------------------------------------------------------- leitura

    /**
     * Lê o arquivo preenchido (.xlsx ou .csv) e valida cada linha contra o PGR e
     * os funcionários já cadastrados ([existentes]).
     */
    suspend fun importar(
        nomeArquivo: String,
        bytes: ByteArray,
        existentes: List<Funcionario>,
        inflate: suspend (ByteArray) -> ByteArray?
    ): ResultadoImportacao {
        val linhas = try {
            if (ehZip(bytes)) lerXlsx(bytes, inflate) else lerCsv(bytes)
        } catch (e: Throwable) {
            return ResultadoImportacao(
                emptyList(),
                e.message ?: "Não foi possível ler \"$nomeArquivo\". Use o modelo .xlsx ou um .csv."
            )
        }
        return validar(linhas, existentes)
    }

    private fun ehZip(b: ByteArray) = b.size > 4 && b[0] == 0x50.toByte() && b[1] == 0x4B.toByte()

    fun validar(linhas: List<List<String>>, existentes: List<Funcionario>): ResultadoImportacao {
        val idxCab = linhas.take(15).indexOfFirst { row ->
            val n = row.map { PgrCatalog.normalizar(it) }
            n.any { it.contains("cpf") } && n.any { it.contains("nome") }
        }
        if (idxCab < 0) {
            return ResultadoImportacao(
                emptyList(),
                "Cabeçalho não encontrado. Use a planilha modelo (colunas Nome completo e CPF são obrigatórias)."
            )
        }
        val cab = linhas[idxCab].map { PgrCatalog.normalizar(it) }
        fun col(vararg chaves: String) = cab.indexOfFirst { h -> chaves.any { h.contains(it) } }
        val cNome = col("nome")
        val cCpf = col("cpf")
        val cMatricula = col("matric")
        val cAdmissao = col("admiss", "data")
        val cCargo = col("cargo", "funcao")

        val porCpf = existentes.associateBy { it.cpf }
        val vistos = HashMap<String, Int>()
        val resultado = ArrayList<LinhaImportacao>()
        for (i in idxCab + 1 until linhas.size) {
            val row = linhas[i]
            fun cel(c: Int) = if (c < 0) "" else row.getOrNull(c).orEmpty().trim()
            if (row.all { it.isBlank() }) continue
            val numero = i + 1
            val erros = ArrayList<String>()

            val nome = cel(cNome).split(Regex("\\s+")).filter { it.isNotEmpty() }.joinToString(" ")
            if (nome.length < 3) erros += "Nome obrigatório (mínimo 3 letras)"

            val cpf = cpfDe(cel(cCpf))
            when {
                cpf.isEmpty() -> erros += "CPF obrigatório"
                !Cpf.valido(cpf) -> erros += "CPF inválido"
                else -> vistos[cpf]?.let { erros += "CPF repetido na planilha (linha $it)" } ?: vistos.put(cpf, numero)
            }

            val matricula = textoDeNumero(cel(cMatricula))
            if (matricula.length > 40) erros += "Matrícula com mais de 40 caracteres"

            val admissaoBruta = cel(cAdmissao)
            val admissao = dataDe(admissaoBruta)
            if (admissaoBruta.isNotEmpty() && admissao == null) erros += "Data de admissão inválida (use dd/mm/aaaa)"

            val cargoTexto = cel(cCargo)
            var cargo: PgrCargoOpcao? = null
            if (cargoTexto.isNotEmpty()) {
                when (val r = cargoDe(cargoTexto)) {
                    is CargoBusca.Achou -> cargo = r.opcao
                    CargoBusca.Ambiguo -> erros += "Cargo existe em mais de um setor: escolha na lista da planilha"
                    CargoBusca.NaoAchou -> erros += "Cargo não encontrado na lista do PGR"
                }
            }

            resultado += LinhaImportacao(
                linha = numero,
                nome = nome,
                cpf = cpf,
                matricula = matricula,
                admissao = admissao.orEmpty(),
                cargo = cargo,
                cargoTexto = cargoTexto,
                erros = erros,
                existente = porCpf[cpf]
            )
        }
        if (resultado.isEmpty()) return ResultadoImportacao(emptyList(), "A planilha não tem colaboradores preenchidos.")
        return ResultadoImportacao(resultado)
    }

    private sealed class CargoBusca {
        class Achou(val opcao: PgrCargoOpcao) : CargoBusca()
        object Ambiguo : CargoBusca()
        object NaoAchou : CargoBusca()
    }

    private fun cargoDe(texto: String): CargoBusca {
        val alvo = PgrCatalog.normalizar(texto).trim()
        val opcoes = PgrCatalog.opcoes
        opcoes.firstOrNull { PgrCatalog.normalizar(rotuloCargo(it)) == alvo }?.let { return CargoBusca.Achou(it) }
        opcoes.firstOrNull { PgrCatalog.normalizar(it.id) == alvo }?.let { return CargoBusca.Achou(it) }
        val nomeCargo = alvo.substringBefore('|').trim()
        val mesmos = opcoes.filter { PgrCatalog.normalizar(it.cargo.nome) == nomeCargo }
        return when {
            mesmos.size == 1 -> CargoBusca.Achou(mesmos[0])
            mesmos.isEmpty() -> CargoBusca.NaoAchou
            else -> {
                val setor = alvo.substringAfter('|', "").trim()
                mesmos.firstOrNull { setor.isNotEmpty() && PgrCatalog.normalizar(it.setorGhe) == setor }
                    ?.let { CargoBusca.Achou(it) } ?: CargoBusca.Ambiguo
            }
        }
    }

    /** CPF vindo do Excel pode ter perdido zeros à esquerda ou virado notação científica. */
    fun cpfDe(raw: String): String {
        val t = raw.trim()
        if (t.isEmpty()) return ""
        val numero = if (Regex("^\\d+(\\.\\d+)?[eE]\\+?\\d+$").matches(t)) {
            t.toDoubleOrNull()?.let { if (it < 1e12) it.toLong().toString() else null } ?: t
        } else t.removeSuffix(".0")
        val d = numero.filter { it.isDigit() }
        return if (d.length in 8..10) d.padStart(11, '0') else d
    }

    private fun textoDeNumero(raw: String): String =
        if (Regex("^\\d+\\.0$").matches(raw)) raw.removeSuffix(".0") else raw

    /** Aceita dd/mm/aaaa, d-m-aa, aaaa-mm-dd e número serial do Excel; devolve dd/mm/aaaa. */
    fun dataDe(raw: String): String? {
        val t = raw.trim()
        if (t.isEmpty()) return ""
        Regex("^(\\d{1,2})[/.-](\\d{1,2})[/.-](\\d{2}|\\d{4})(\\s.*)?$").find(t)?.let { m ->
            var ano = m.groupValues[3].toInt()
            if (m.groupValues[3].length == 2) ano += if (ano > 50) 1900 else 2000
            return montarData(m.groupValues[1].toInt(), m.groupValues[2].toInt(), ano)
        }
        Regex("^(\\d{4})-(\\d{1,2})-(\\d{1,2})([T\\s].*)?$").find(t)?.let { m ->
            return montarData(m.groupValues[3].toInt(), m.groupValues[2].toInt(), m.groupValues[1].toInt())
        }
        val serial = t.toDoubleOrNull() ?: return null
        if (serial < 1 || serial > 2958465) return null
        val (a, mes, dia) = civilDeDias(serial.toLong() - 25569)
        return montarData(dia, mes, a)
    }

    private fun montarData(dia: Int, mes: Int, ano: Int): String? {
        if (ano !in 1900..2100 || mes !in 1..12) return null
        val bissexto = (ano % 4 == 0 && ano % 100 != 0) || ano % 400 == 0
        val max = when (mes) {
            2 -> if (bissexto) 29 else 28
            4, 6, 9, 11 -> 30
            else -> 31
        }
        if (dia !in 1..max) return null
        return "${dia.toString().padStart(2, '0')}/${mes.toString().padStart(2, '0')}/$ano"
    }

    /** Dias desde 1970-01-01 → (ano, mês, dia). */
    private fun civilDeDias(dias: Long): Triple<Int, Int, Int> {
        val z = dias + 719468
        val era = (if (z >= 0) z else z - 146096) / 146097
        val doe = z - era * 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = (doy - (153 * mp + 2) / 5 + 1).toInt()
        val m = (if (mp < 10) mp + 3 else mp - 9).toInt()
        val y = (yoe + era * 400 + (if (m <= 2) 1 else 0)).toInt()
        return Triple(y, m, d)
    }

    // ---------------------------------------------------------------- CSV

    fun lerCsv(bytes: ByteArray): List<List<String>> {
        var texto = bytes.decodeToString()
        if (texto.contains('\uFFFD')) texto = latin1(bytes)
        texto = texto.removePrefix("\uFEFF")
        val primeira = texto.substringBefore('\n')
        val sep = if (primeira.count { it == ';' } >= primeira.count { it == ',' }) ';' else ','
        val linhas = ArrayList<List<String>>()
        var atual = ArrayList<String>()
        val campo = StringBuilder()
        var aspas = false
        var i = 0
        while (i < texto.length) {
            val ch = texto[i]
            if (aspas) {
                if (ch == '"') {
                    if (i + 1 < texto.length && texto[i + 1] == '"') {
                        campo.append('"'); i++
                    } else aspas = false
                } else campo.append(ch)
            } else when (ch) {
                '"' -> aspas = true
                sep -> { atual.add(campo.toString()); campo.clear() }
                '\r' -> {}
                '\n' -> {
                    atual.add(campo.toString()); campo.clear()
                    linhas.add(atual); atual = ArrayList()
                }
                else -> campo.append(ch)
            }
            i++
        }
        if (campo.isNotEmpty() || atual.isNotEmpty()) {
            atual.add(campo.toString())
            linhas.add(atual)
        }
        return linhas
    }

    private fun latin1(bytes: ByteArray): String {
        val cp1252 = mapOf(0x80 to '€', 0x8A to 'Š', 0x8C to 'Œ', 0x96 to '–', 0x97 to '—', 0x9A to 'š', 0x9C to 'œ')
        return buildString(bytes.size) {
            for (b in bytes) {
                val v = b.toInt() and 0xFF
                append(cp1252[v] ?: Char(v))
            }
        }
    }

    // ---------------------------------------------------------------- XLSX

    private fun attr(tag: String, nome: String): String? =
        Regex("""(?:^|\s)(?:\w+:)?$nome\s*=\s*"([^"]*)"""").find(tag)?.groupValues?.get(1)

    suspend fun lerXlsx(b: ByteArray, inflate: suspend (ByteArray) -> ByteArray?): List<List<String>> {
        val zip = ZipLeitor.indice(b)
        suspend fun texto(nome: String): String? = zip[nome]?.let { ZipLeitor.ler(b, it, inflate).decodeToString() }

        val workbook = texto("xl/workbook.xml") ?: error("Arquivo .xlsx sem planilha (workbook).")
        val rels = texto("xl/_rels/workbook.xml.rels").orEmpty()
        val alvos = Regex("<Relationship\\b([^>]*)>").findAll(rels).mapNotNull { m ->
            val id = attr(m.groupValues[1], "Id") ?: return@mapNotNull null
            val alvo = attr(m.groupValues[1], "Target") ?: return@mapNotNull null
            id to (if (alvo.startsWith("/")) alvo.drop(1) else "xl/$alvo")
        }.toMap()
        val abas = Regex("<sheet\\b([^>]*)>").findAll(workbook).mapNotNull { m ->
            val nome = decodificar(attr(m.groupValues[1], "name").orEmpty())
            val rid = attr(m.groupValues[1], "id") ?: return@mapNotNull null
            nome to alvos[rid]
        }.toList()
        val caminho = (abas.firstOrNull { PgrCatalog.normalizar(it.first) == PgrCatalog.normalizar(ABA_DADOS) } ?: abas.firstOrNull())
            ?.second ?: "xl/worksheets/sheet1.xml"
        val folha = texto(caminho) ?: error("Aba de colaboradores não encontrada no .xlsx.")

        val compartilhadas = texto("xl/sharedStrings.xml")?.let { xml ->
            Regex("<si\\b[^>]*>([\\s\\S]*?)</si>").findAll(xml).map { textoRico(it.groupValues[1]) }.toList()
        }.orEmpty()

        val linhas = ArrayList<List<String>>()
        var proximaLinha = 1
        Regex("<row\\b([^>]*?)(?:/>|>([\\s\\S]*?)</row>)").findAll(folha).forEach { rm ->
            val numero = attr(rm.groupValues[1], "r")?.toIntOrNull() ?: proximaLinha
            proximaLinha = numero + 1
            val celulas = ArrayList<String>()
            var proximaCol = 0
            Regex("<c\\b([^>]*?)(?:/>|>([\\s\\S]*?)</c>)").findAll(rm.groupValues[2]).forEach { cm ->
                val a = cm.groupValues[1]
                val corpo = cm.groupValues[2]
                val col = attr(a, "r")?.let { ref -> indiceColuna(ref) } ?: proximaCol
                proximaCol = col + 1
                val v = Regex("<v>([\\s\\S]*?)</v>").find(corpo)?.groupValues?.get(1)?.let(::decodificar)
                val valor = when (attr(a, "t")) {
                    "s" -> v?.toIntOrNull()?.let { compartilhadas.getOrNull(it) }.orEmpty()
                    "inlineStr" -> textoRico(corpo)
                    "b" -> if (v == "1") "VERDADEIRO" else "FALSO"
                    else -> v.orEmpty()
                }
                while (celulas.size < col) celulas.add("")
                if (col < celulas.size) celulas[col] = valor else celulas.add(valor)
            }
            while (linhas.size < numero - 1) linhas.add(emptyList())
            linhas.add(celulas)
        }
        return linhas
    }

    private fun indiceColuna(ref: String): Int {
        var n = 0
        for (ch in ref) {
            if (ch !in 'A'..'Z') break
            n = n * 26 + (ch - 'A' + 1)
        }
        return n - 1
    }

    private fun textoRico(xml: String): String {
        val semFonetica = xml.replace(Regex("<rPh\\b[\\s\\S]*?</rPh>"), "")
        return Regex("<t\\b[^>]*>([\\s\\S]*?)</t>").findAll(semFonetica)
            .joinToString("") { decodificar(it.groupValues[1]) }
    }

    private fun decodificar(s: String): String {
        if (!s.contains('&') && !s.contains("_x")) return s
        return s.replace(Regex("&(#x[0-9a-fA-F]+|#\\d+|lt|gt|amp|quot|apos);")) { m ->
            val k = m.groupValues[1]
            when {
                k == "lt" -> "<"
                k == "gt" -> ">"
                k == "amp" -> "&"
                k == "quot" -> "\""
                k == "apos" -> "'"
                k.startsWith("#x") -> k.drop(2).toIntOrNull(16)?.let { Char(it).toString() }.orEmpty()
                else -> k.drop(1).toIntOrNull()?.let { Char(it).toString() }.orEmpty()
            }
        }.replace(Regex("_x([0-9A-Fa-f]{4})_")) { m ->
            val c = m.groupValues[1].toInt(16)
            if (c == 0x0D || c == 0x0A || c == 0x09) " " else Char(c).toString()
        }
    }
}
